import TimerIcon from '@mui/icons-material/Timer';
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  type DialogProps,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '~/components/Button';
import { Link } from '~/components/Link';
import useRoute from '~/hooks/useRoute';
import useRouter from '~/hooks/useRouter';

interface SearchLoadingDialogProps extends Omit<DialogProps, 'open'> {
  open: boolean;
  retryAfter: number;
  onCancel: () => void;
  totalRetries: number;
}

export const SearchLoadingDialog = ({
  open,
  retryAfter,
  onCancel,
  totalRetries,
  ...dialogProps
}: SearchLoadingDialogProps) => {
  const [seconds, setSeconds] = useState(retryAfter * totalRetries);
  const [retryCount, setRetryCount] = useState(1);
  const route = useRoute();
  const intervalRef = useRef<ReturnType<typeof setInterval> | undefined>(
    undefined
  );
  const router = useRouter();

  const timerText = useMemo(() => {
    const minutes = '0' + Math.floor(seconds / 60);
    const remainingSeconds = '0' + (seconds - +minutes * 60);
    return minutes.slice(-2) + ':' + remainingSeconds.slice(-2);
  }, [seconds]);

  const initialized = useRef(false);

  const retry = useCallback(() => {
    let currentAttempt = +(route.query.retryAfter || '0');
    ++currentAttempt;
    // Defer router update to avoid setState during render
    setTimeout(() => {
      router.replace({
        path: route.path,
        query: {
          ...route.query,
          retryAfter: currentAttempt,
        },
      });
    }, 0);
  }, [router, route.path, route.query]);

  useEffect(() => {
    if (!open) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      initialized.current = false;
      return;
    }

    if (initialized.current) {
      return;
    }
    initialized.current = true;

    intervalRef.current = setInterval(() => {
      if (retryCount > totalRetries) {
        onCancel();
        clearInterval(intervalRef.current);
        return;
      }
      setSeconds((prev) => {
        const newSeconds = prev - 1;
        if (newSeconds % retryAfter === 0) {
          setRetryCount((prev) => {
            const newRetryCount = prev + 1;
            if (newSeconds === 0) {
              window.location.reload();
            }
            return newRetryCount;
          });
          retry();
        }
        return newSeconds;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        initialized.current = false;
        clearInterval(intervalRef.current);
      }
    };
  }, [open, retryCount, retryAfter, retry, totalRetries, onCancel]);

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      {...dialogProps}>
      <DialogTitle>
        <CircularProgress
          size={20}
          sx={{ verticalAlign: 'middle', marginTop: '-4px', marginRight: 1 }}
        />
        Your search is taking longer than expected
      </DialogTitle>
      <DialogContent>
        Please wait while the results are loading. Sometimes larger reports, or
        a large number of filters can take a minute or two.
      </DialogContent>
      <DialogContent>
        If you don't see results within 5 minutes, send us an email:{' '}
        <Link
          href={`mailto:support@nerdydata.com?subject=Search%20Timeout&body=${encodeURIComponent(
            route.href
          )}`}
          target="_blank"
          rel="noopener noreferrer">
          support@nerdydata.com
        </Link>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onCancel}>
          Go Back
        </Button>
        <Chip
          icon={<TimerIcon />}
          label={`${timerText}`}
          variant="outlined"
          disabled
        />
      </DialogActions>
    </Dialog>
  );
};
